// sim/genome.ts — the caste GENOME system: slot genes into a caste template and
// resolve them into an effective CasteProfile that future spawns inherit.
//
// Integration seam with the gene-loot layer (src/sim/geneData.ts, src/types.ts):
// that layer mints genes as effects over EFFECT_CHANNEL (Movement/Combat/Sensing/
// Defense/Toxicity/Regen/Size). THIS file owns the other half — how a slotted
// gene's channel vector maps onto the engine's caste knobs (speed, flight, hp,
// carry, attack, …) and how genomes are mutated. A loot Gene reduces to a
// CasteGene via `geneChannels`; a hand-authored gene can set channels directly.
//
// Non-retroactive by construction: the GenomeSystem keeps an append-only list of
// immutable profile SNAPSHOTS. A genome edit publishes a NEW snapshot; existing
// units keep the snapshot id they captured at spawn. Determinism: pure integer
// math, no RNG, fixed iteration — genome edits happen off the hot path.

import { EFFECT_CHANNEL, EFFECT_CHANNEL_COUNT, GENE_TYPE_COUNT } from '../types.ts';
import type { CasteConfig, Caste, GeneType, GeneTrait, Rarity, EntityId } from '../types.ts';
import {
  GENE_TYPE_BASE_CHANNEL,
  TRAIT_CHANNEL,
  TRAIT_MAGNITUDE,
  TRAIT_CHANNEL2,
  TRAIT_MAGNITUDE2,
  RARITY_MAG_BONUS,
} from './geneData.ts';
import { KIND } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';

// ---------------------------------------------------------------------------
// Effective caste profile — what behaviours actually read (a CasteConfig plus
// the gene-granted movement/extra channels). This is the resolved template.
// ---------------------------------------------------------------------------

export interface CasteProfile {
  readonly startEnergy: number;
  readonly energyDecay: number;
  readonly startHp: number;
  readonly senseRadius: number;
  readonly carryCapacity: number;
  readonly attackPower: number;
  readonly roamRadius: number;
  // --- movement style ---
  /** Steps attempted per tick toward a target (>=1). Higher = faster. */
  readonly stepsPerTick: number;
  /**
   * Fixed-point movement speed (SPEED_UNIT = 1 tile/tick); only consumed when the
   * creature-speed feature is on (OrganismCtx.creatureSpeed), else `stepsPerTick`
   * drives movement (byte-identical). Lets a creature move SUB-tile (slow tank) or
   * smoothly fast via a per-entity sub-tick accumulator. Integer (fixed-point).
   */
  readonly speed: number;
  /** Ignores walls + tile cap when moving (Wing genes grant this). */
  readonly flying: boolean;
  // --- extras ---
  /** Hit points regenerated per tick, up to startHp (Regenerative). */
  readonly regen: number;
  /** Visual/size scalar (Enlarged) — reserved for future rendering. */
  readonly size: number;
  /** Toxic potency (Venom) — folded into attack for now; reserved for DoT. */
  readonly toxicity: number;
}

/**
 * Fixed-point movement scale: SPEED_UNIT == 1 tile/tick. A creature with
 * `speed === k * SPEED_UNIT` (k a non-negative integer) moves EXACTLY k tiles/tick
 * with a sub-tick accumulator that folds back to 0 every tick — byte-identical to
 * the old `for s < stepsPerTick` loop with stepsPerTick == k. Fractional speeds
 * (e.g. SPEED_UNIT/2) carry their remainder across ticks (slow movement).
 */
export const SPEED_UNIT = 256;

/** Build the gene-free base profile for a caste from its config. */
export function baseProfile(cfg: CasteConfig, flying: boolean): CasteProfile {
  return {
    startEnergy: cfg.startEnergy,
    energyDecay: cfg.energyDecay,
    startHp: cfg.startHp,
    senseRadius: cfg.senseRadius,
    carryCapacity: cfg.carryCapacity,
    attackPower: cfg.attackPower,
    roamRadius: cfg.roamRadius,
    stepsPerTick: 1,
    // base stepsPerTick is 1 ⇒ base speed is exactly 1 tile/tick (SPEED_UNIT). A
    // zero-gene creature therefore moves identically whether the feature is on or off.
    speed: 1 * SPEED_UNIT,
    flying,
    regen: 0,
    size: 0,
    toxicity: 0,
  };
}

// --- Channel → profile mapping (the engine's interpretation of genes) -------
// Tunable constants; all integer so profiles stay hashable/deterministic.
const MOVE_PER_STEP = 2; // +2 Movement ⇒ +1 step/tick
const FLY_THRESHOLD = 6; // Movement >= this grants flight (big wings)
const DECAY_RELIEF = 3; // +3 Movement ⇒ -1 energy/tick (lighter = cheaper)
const HP_PER_DEFENSE = 2; // +1 Defense ⇒ +2 hp

// --- Fixed-point speed mapping (consumed ONLY when creatureSpeed is on) -------
// All UNTUNED placeholders; integer (fixed-point), so the `speed` field stays
// hashable/deterministic. Movement RAISES speed (finer-grained than the old
// +2⇒+1-step quantum), Size LOWERS it (the slow-tank tradeoff). With NO Movement
// and NO Size, speed === base.stepsPerTick * SPEED_UNIT, so an un-evolved creature
// in on-mode moves identically to off-mode. Negative Movement ⇒ slower than base
// (reaches the slow regime), so a tank caste is genuinely reachable.
const SPEED_PER_MOVE = 64; // each +1 Movement ⇒ +SPEED_UNIT/4 (4 Movement ≈ +1 tile/tick) — untuned
const SPEED_PER_SIZE = 48; // each +1 Size ⇒ -3/16 tile/tick (heavier = slower) — untuned
const MIN_SPEED = SPEED_UNIT / 4; // never fully frozen: a creature always crawls ≥ 1 tile / 4 ticks — untuned
const MAX_SPEED = 4 * SPEED_UNIT; // cap at 4 tiles/tick so stacked Movement can't explode — untuned

/**
 * Resolve the fixed-point `speed` from the base profile + summed channels.
 * Pure integer arithmetic (no float stored). Movement (signed) scales speed up or
 * down; Size scales it down. Clamped to [MIN_SPEED, MAX_SPEED]. With mv == 0 and
 * sz == 0 this returns `base.stepsPerTick * SPEED_UNIT` exactly, so a zero-gene
 * creature moves identically with the feature on or off (the gate's whole point).
 */
function resolveSpeed(base: CasteProfile, mv: number, sz: number): number {
  const raw = base.stepsPerTick * SPEED_UNIT + mv * SPEED_PER_MOVE - Math.max(0, sz) * SPEED_PER_SIZE;
  return raw < MIN_SPEED ? MIN_SPEED : raw > MAX_SPEED ? MAX_SPEED : raw;
}

/**
 * Resolve a base profile + summed channel effects into an effective profile.
 * Covers all four required dimensions: behaviour params, movement style,
 * resource efficiency (carry + decay), and combat role (attack + hp).
 */
export function applyChannels(base: CasteProfile, ch: readonly number[]): CasteProfile {
  const mv = ch[EFFECT_CHANNEL.Movement] ?? 0;
  const cb = ch[EFFECT_CHANNEL.Combat] ?? 0;
  const sn = ch[EFFECT_CHANNEL.Sensing] ?? 0;
  const df = ch[EFFECT_CHANNEL.Defense] ?? 0;
  const tx = ch[EFFECT_CHANNEL.Toxicity] ?? 0;
  const rg = ch[EFFECT_CHANNEL.Regen] ?? 0;
  const sz = ch[EFFECT_CHANNEL.Size] ?? 0;
  const mvPos = mv > 0 ? mv : 0;
  return {
    startEnergy: base.startEnergy,
    energyDecay: Math.max(0, base.energyDecay - Math.floor(mvPos / DECAY_RELIEF)),
    startHp: Math.max(1, base.startHp + df * HP_PER_DEFENSE),
    senseRadius: Math.max(0, base.senseRadius + sn),
    // keep foragers able to carry at least 1 if they ever could
    carryCapacity: Math.max(base.carryCapacity > 0 ? 1 : 0, base.carryCapacity + sz),
    attackPower: Math.max(0, base.attackPower + cb + Math.max(0, tx)),
    roamRadius: base.roamRadius,
    stepsPerTick: Math.max(1, base.stepsPerTick + Math.floor(mvPos / MOVE_PER_STEP)),
    // Fixed-point speed for the opt-in creature-speed feature (off-mode ignores it;
    // stepsPerTick above is unchanged ⇒ byte-identical when the feature is off). With
    // mv == 0 && sz == 0 this equals base.stepsPerTick * SPEED_UNIT (zero-gene parity).
    speed: resolveSpeed(base, mv, sz),
    flying: base.flying || mv >= FLY_THRESHOLD,
    regen: Math.max(0, base.regen + rg),
    size: Math.max(0, base.size + sz),
    toxicity: Math.max(0, base.toxicity + tx),
  };
}

// ---------------------------------------------------------------------------
// Genes (as consumed by a caste genome). Slot == the gene's body-part type, so
// a caste holds at most one gene per GENE_TYPE slot. `channels` is the effect
// vector; `conflictGroup` (optional) drives mutation-conflict resolution.
// ---------------------------------------------------------------------------

export interface CasteGene {
  readonly id: string;
  readonly name: string;
  /** Slot = body-part type (GENE_TYPE.*). One gene per slot. */
  readonly slot: GeneType;
  /** Signed effect per EFFECT_CHANNEL (length EFFECT_CHANNEL_COUNT). */
  readonly channels: readonly number[];
  /** Mutually-exclusive group; '' = no conflict. */
  readonly conflictGroup: string;
}

/**
 * Reduce a loot gene (base type + stacked traits + rarity) to a channel vector,
 * using the gene-loot data tables. This is the bridge from the loot layer's
 * Gene items into the genome — same reduction a hand-authored gene would use.
 */
export function geneChannels(
  baseType: GeneType,
  traits: readonly GeneTrait[],
  rarity: Rarity,
): number[] {
  const ch = new Array<number>(EFFECT_CHANNEL_COUNT).fill(0);
  // The base body-part lends a small baseline to its primary channel.
  const baseCh = GENE_TYPE_BASE_CHANNEL[baseType] as number;
  ch[baseCh] = (ch[baseCh] as number) + 1;
  const bonus = RARITY_MAG_BONUS[rarity] as number;
  for (const t of traits) {
    const c1 = TRAIT_CHANNEL[t] as number;
    ch[c1] = (ch[c1] as number) + (TRAIT_MAGNITUDE[t] as number) + bonus;
    const c2 = TRAIT_CHANNEL2[t] as number;
    if (c2 >= 0) ch[c2] = (ch[c2] as number) + (TRAIT_MAGNITUDE2[t] as number);
  }
  return ch;
}

/** Author a gene straight from traits (e.g. a leg gene = Limb + Lightweight). */
export function geneFromTraits(
  name: string,
  baseType: GeneType,
  traits: readonly GeneTrait[],
  rarity: Rarity,
  conflictGroup = '',
): CasteGene {
  return { id: name, name, slot: baseType, channels: geneChannels(baseType, traits, rarity), conflictGroup };
}

/** Author a gene from an explicit channel vector. */
export function geneFromChannels(
  name: string,
  slot: GeneType,
  channels: readonly number[],
  conflictGroup = '',
): CasteGene {
  return { id: name, name, slot, channels, conflictGroup };
}

/** How to resolve a mutation conflict when adding a gene. */
export const CONFLICT_POLICY = { Reject: 0, Replace: 1 } as const;
export type ConflictPolicy = (typeof CONFLICT_POLICY)[keyof typeof CONFLICT_POLICY];

/** Result of a genome mutation. */
export interface GenomeEdit {
  readonly ok: boolean;
  /** Why it failed ('slot-occupied' | 'conflict' | '') or '' on success. */
  readonly reason: string;
  /** Gene(s) displaced by the edit (conflict eviction or replace), if any. */
  readonly evicted: readonly CasteGene[];
}

// ---------------------------------------------------------------------------
// A single caste's genome: one gene per body-part slot, resolved to a profile.
// ---------------------------------------------------------------------------

export class CasteGenome {
  readonly base: CasteProfile;
  private readonly slots: (CasteGene | null)[];

  constructor(base: CasteProfile) {
    this.base = base;
    this.slots = new Array<CasteGene | null>(GENE_TYPE_COUNT).fill(null);
  }

  geneAt(slot: GeneType): CasteGene | null {
    return this.slots[slot] ?? null;
  }

  /** First OTHER slot whose gene shares conflict group `g`, or -1. */
  private conflictSlot(g: string, exclude: GeneType): number {
    if (g === '') return -1;
    for (let s = 0; s < this.slots.length; s++) {
      if (s === exclude) continue;
      const gene = this.slots[s] ?? null;
      if (gene && gene.conflictGroup === g) return s;
    }
    return -1;
  }

  /** Add a gene into its slot. Slot must be empty (use replace otherwise). */
  add(gene: CasteGene, policy: ConflictPolicy): GenomeEdit {
    if (this.slots[gene.slot]) {
      return { ok: false, reason: 'slot-occupied', evicted: [] };
    }
    return this.place(gene, policy);
  }

  /** Replace whatever is in `slot` with `gene` (no-op-safe if same). */
  replace(slot: GeneType, gene: CasteGene, policy: ConflictPolicy): GenomeEdit {
    if (gene.slot !== slot) return { ok: false, reason: 'slot-mismatch', evicted: [] };
    const prev = this.slots[slot] ?? null;
    this.slots[slot] = null; // clear first so conflict scan ignores the outgoing gene
    const res = this.place(gene, policy);
    if (!res.ok) {
      this.slots[slot] = prev; // restore on rejection
      return res;
    }
    return prev ? { ok: true, reason: '', evicted: [prev, ...res.evicted] } : res;
  }

  /** Remove the gene in `slot`; returns it (or null if empty). */
  remove(slot: GeneType): CasteGene | null {
    const prev = this.slots[slot] ?? null;
    this.slots[slot] = null;
    return prev;
  }

  /** Shared placement w/ conflict handling. Assumes the slot is clear. */
  private place(gene: CasteGene, policy: ConflictPolicy): GenomeEdit {
    const cs = this.conflictSlot(gene.conflictGroup, gene.slot);
    if (cs >= 0) {
      if (policy === CONFLICT_POLICY.Reject) {
        return { ok: false, reason: 'conflict', evicted: [] };
      }
      const evicted = this.slots[cs] as CasteGene;
      this.slots[cs] = null; // Replace policy: evict the conflicting gene
      this.slots[gene.slot] = gene;
      return { ok: true, reason: '', evicted: [evicted] };
    }
    this.slots[gene.slot] = gene;
    return { ok: true, reason: '', evicted: [] };
  }

  /** Sum the channel vectors of all slotted genes. */
  channelTotals(): number[] {
    const total = new Array<number>(EFFECT_CHANNEL_COUNT).fill(0);
    for (const gene of this.slots) {
      if (!gene) continue;
      for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) {
        total[c] = (total[c] as number) + (gene.channels[c] ?? 0);
      }
    }
    return total;
  }

  /** Resolve the current loadout into an effective caste profile. */
  profile(): CasteProfile {
    return applyChannels(this.base, this.channelTotals());
  }

  /**
   * Resolve the profile AS IF `channels` were the gene occupying `slot` (replacing
   * whatever currently sits there), WITHOUT mutating the genome. The apply-time power
   * budget (src/sim/genePowerBudget.ts) uses this to measure a candidate gene's realized
   * caste-power contribution before committing it. Pure; allocates one scratch array.
   */
  previewProfile(slot: GeneType, channels: readonly number[]): CasteProfile {
    const total = new Array<number>(EFFECT_CHANNEL_COUNT).fill(0);
    for (let s = 0; s < this.slots.length; s++) {
      if (s === slot) continue;
      const gene = this.slots[s] ?? null;
      if (!gene) continue;
      for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) total[c] = (total[c] as number) + (gene.channels[c] ?? 0);
    }
    for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) total[c] = (total[c] as number) + (channels[c] ?? 0);
    return applyChannels(this.base, total);
  }
}

// ---------------------------------------------------------------------------
// GenomeSystem — one genome per caste + append-only profile snapshots.
// Spawns capture currentProfileId(caste); edits publish a fresh snapshot so the
// change reaches only FUTURE spawns (non-retroactive).
// ---------------------------------------------------------------------------

export class GenomeSystem {
  private readonly genomes: CasteGenome[];
  /** Append-only immutable snapshots; index = a profile id. */
  private readonly profiles: CasteProfile[] = [];
  /** Current snapshot id per caste. */
  private readonly currentId: Int32Array;

  constructor(baseProfiles: readonly CasteProfile[]) {
    this.genomes = baseProfiles.map((b) => new CasteGenome(b));
    this.currentId = new Int32Array(baseProfiles.length);
    for (let caste = 0; caste < baseProfiles.length; caste++) {
      this.currentId[caste] = this.profiles.length;
      this.profiles.push((this.genomes[caste] as CasteGenome).profile());
    }
  }

  private resnapshot(caste: Caste): void {
    this.currentId[caste] = this.profiles.length;
    this.profiles.push((this.genomes[caste] as CasteGenome).profile());
  }

  addGene(caste: Caste, gene: CasteGene, policy: ConflictPolicy = CONFLICT_POLICY.Reject): GenomeEdit {
    const r = (this.genomes[caste] as CasteGenome).add(gene, policy);
    if (r.ok) this.resnapshot(caste);
    return r;
  }

  replaceGene(
    caste: Caste,
    slot: GeneType,
    gene: CasteGene,
    policy: ConflictPolicy = CONFLICT_POLICY.Replace,
  ): GenomeEdit {
    const r = (this.genomes[caste] as CasteGenome).replace(slot, gene, policy);
    if (r.ok) this.resnapshot(caste);
    return r;
  }

  removeGene(caste: Caste, slot: GeneType): CasteGene | null {
    const g = (this.genomes[caste] as CasteGenome).remove(slot);
    if (g) this.resnapshot(caste);
    return g;
  }

  geneAt(caste: Caste, slot: GeneType): CasteGene | null {
    return (this.genomes[caste] as CasteGenome).geneAt(slot);
  }

  /** Profile id a NEW member of `caste` would be stamped with. */
  currentProfileId(caste: Caste): number {
    return this.currentId[caste] as number;
  }

  /** Look up an immutable profile snapshot by id. */
  profile(id: number): CasteProfile {
    return this.profiles[id] as CasteProfile;
  }

  /** The caste's live (latest) profile. */
  currentProfile(caste: Caste): CasteProfile {
    return this.profiles[this.currentId[caste] as number] as CasteProfile;
  }

  /** Resolve `caste`'s profile as if `channels` occupied `slot` — a non-mutating preview. */
  previewProfile(caste: Caste, slot: GeneType, channels: readonly number[]): CasteProfile {
    return (this.genomes[caste] as CasteGenome).previewProfile(slot, channels);
  }

  /** Number of published snapshots (for tests/debug). */
  get snapshotCount(): number {
    return this.profiles.length;
  }
}

/**
 * Opt-in RETROACTIVE re-tune: re-stamp every live organism of `caste` to the
 * caste's current profile (the "...unless specified" escape hatch). O(live).
 */
export function restampCaste(entities: EntitySystem, genomes: GenomeSystem, caste: Caste): void {
  const pid = genomes.currentProfileId(caste);
  const active = entities.activeView();
  const n = entities.aliveCount;
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Organism) continue;
    if ((entities.caste[id] as number) !== caste) continue;
    entities.profileRef[id] = pid;
  }
}

/**
 * Default IMMEDIATE magnitude (percent of full) the living members of a caste feel the
 * instant a gene is applied. The FULL gene still reaches future spawns — this is just
 * the "you feel it now" taste, deliberately smaller so a freshly raised brood is stronger.
 */
export const GENE_IMMEDIATE_PERCENT = 35;

/**
 * Apply a looted gene with BOTH an immediate and a generational effect, so a player
 * applying a gene is rewarded NOW instead of only in future broods:
 *  - GENERATIONAL — the FULL gene enters the caste genome, so every FUTURE spawn of this
 *    caste inherits it in full (the non-retroactive snapshot seam, unchanged).
 *  - IMMEDIATE — the currently-LIVING members of the caste are re-stamped onto a snapshot
 *    carrying a SCALED-DOWN (`immediatePercent`) version of the same gene, so the existing
 *    colony instantly gains a partial stat bump AND visibly mutates (sprites key on the
 *    profile snapshot). Smaller than full on purpose, so a fresh generation is still the
 *    stronger one — the incentive to keep breeding survives.
 *
 * `fullImmediate` (the opt-in "apply fully to living now" toggle) skips the scaling and
 * gives the living members the FULL gene immediately.
 *
 * Deterministic + replay-safe: pure integer channel math, fixed op order, mutates only the
 * append-only snapshot list + `profileRef`. Two runs of the same action reproduce identically.
 */
export function applyGeneStaged(
  entities: EntitySystem,
  genomes: GenomeSystem,
  caste: Caste,
  gene: CasteGene,
  immediatePercent: number,
  fullImmediate: boolean,
): void {
  // `replaceGene` (not `addGene`) so this works whether the slot is empty (first gene of
  // this body-part) OR already occupied (re-applying the same part) — `addGene` no-ops a
  // filled slot, which would strand the partial as the "final" gene.
  if (fullImmediate) {
    genomes.replaceGene(caste, gene.slot, gene, CONFLICT_POLICY.Replace);
    restampCaste(entities, genomes, caste);
    return;
  }
  // IMMEDIATE: a scaled-down gene in the slot → snapshot → re-stamp the living members.
  // `trunc` shrinks magnitude toward zero, so a trade-off gene's penalty also softens.
  const scaled = gene.channels.map((c) => Math.trunc((c * immediatePercent) / 100));
  const partial = geneFromChannels(gene.name, gene.slot, scaled, gene.conflictGroup);
  genomes.replaceGene(caste, gene.slot, partial, CONFLICT_POLICY.Replace);
  restampCaste(entities, genomes, caste);
  // GENERATIONAL: replace the partial with the FULL gene → a fresh snapshot that future
  // spawns capture. The living members keep their (immutable) partial snapshot.
  genomes.replaceGene(caste, gene.slot, gene, CONFLICT_POLICY.Replace);
}
